import { Component, Inject, OnInit, PLATFORM_ID, Signal, WritableSignal, computed, inject, signal, viewChild } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { CarouselComponent } from '../shared/carousel/carousel.component';
import { ProductsService } from './products.service';
import { NotificationService } from '../shared/notification/notification.service';
import { SignalsStoreService } from '../shared/signals-store.service';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { StockService } from '../stock/stock.service';
import { LocalStorageService } from '../shared/local-storage.service';
import { toObservable } from '@angular/core/rxjs-interop';
import { NotificationsComponent } from '../shared/notification/notification.component';
import { tap } from 'rxjs';
import { ApiResponse } from '../shared/common/types';
import { KlaviyoService } from '../shared/klaviyo/klaviyo.service';
import { KLAVIYOEVENTS } from '../shared/klaviyo/events';
import { capitalizeWords } from '../shared/common/utils';
import { environment } from '../../environments/environment';
import { FirebaseCrudService } from '../shared/firebase-crud.service';
import { ProductCardV2Component } from '../shared/product-card-v2/product-card-v2.component';
import { Meta, Title } from '@angular/platform-browser';
import { DataLayerService } from '../shared/data-layer/data-layer.service';
import { Product } from './product.types';
import { DATA_LAYER_ECOMMERCE_CURRENCY_OPTIONS } from '../shared/data-layer/data-layer.types';

@Component({
  selector: 'app-product',
  imports: [
    RouterModule,
    ProductCardV2Component,
    CarouselComponent,
    CommonModule,
    MatProgressBarModule,
    NotificationsComponent
  ],
  providers: [
    NotificationService,
    ProductsService,
  ],
  templateUrl: './product.component.html',
  styleUrl: './product.component.scss'
})

export class ProductComponent implements OnInit {
  #meta = inject(Meta);
  #title = inject(Title);
  private productCard = viewChild.required<ProductCardV2Component>('productCard');
  private route = inject(ActivatedRoute);
  private productsService = inject(ProductsService);
  signalsStoreService = inject(SignalsStoreService);
  private router = inject(Router);
  localStorageService = inject(LocalStorageService)
  stockService = inject(StockService)
  private klaviyoService = inject(KlaviyoService);
  private firebaseCrudService = inject(FirebaseCrudService);
  #dataLayerService = inject(DataLayerService);

  seoURL!: string;
  isContentLoaded: WritableSignal<boolean> = signal(false);

  showDiscountTag = signal(false);
  product: Signal<any> = computed(() => this.productsService.productSignal() || null)
  productCardSummary: Signal<any> = computed(() => this.setUpProductCardSummary());
  carouselItems: Signal<any> = computed(() => this.setUpCarouselItems());
  productImages: Signal<any[]> = computed(() => this.setUpProductImages());
  updateIsDislike = signal(false);
  isFavorite = signal<boolean>(false);
  isDislike = signal<boolean>(false);
  selectedAttribute: WritableSignal<any> = signal('');

  productId: WritableSignal<number> = signal(0);
  stockSignal = computed(() => this.stockService.mapStockSignal());
  obsStockSignal = toObservable(this.stockSignal);
  variant: WritableSignal<any> = signal(null);

  likeTotals = computed(() => this.setUpFavTotals());
  showMultipleImages = signal(environment.config.flows.productDetail?.showMultipleImages ?? true);
  private stockMapKey: any = null;

  constructor(@Inject(PLATFORM_ID) private platformId: any) { }

  ngOnInit(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    this.handleRouteChanges();
    this.isContentLoaded = this.signalsStoreService.isContentLoaded;
  }

  private setUpSEOTags(product: string) {
    const { baseTitle, baseURL, type } = environment.config.seo;
    const url = `shop/${product}`
    const title = `${capitalizeWords(product.replace(/\//g, ' | '))} | ${baseTitle}`;

    // Page title
    this.#title.setTitle(title);

    // Open Graph for social networks:
    this.#meta.addTag({ property: 'og:title', content: title });
    this.#meta.addTag({ property: 'og:url', content: `${baseURL}${url}` });
    this.#meta.addTag({ property: 'og:type', content: type });
  }

  private handleRouteChanges(): any {
    this.route.params.subscribe((params) => {
      this.seoURL = `${params['category']}/${params['subcategory']}/${params['product']}`;
      this.setUpSEOTags(this.seoURL);
      this.productsService.getProductByUrl(this.seoURL).pipe(
        tap(({ data: product }: ApiResponse<any>) => {
          if (product) {
            this.klaviyoService.trackEvent(KLAVIYOEVENTS.ProductViewed, {
              bundle: product.bundle?.items || [],
              category: product.category,
              id: product.id,
              name: product.name,
              price: product.price,
              productUrl: product.productUrl,
              size: product.size || '',
              subcategory: product.subCategory,
              originalPrice: product.originalPrice,
              producer: product.producer,
              specialCategory: product.specialCategory,
              tags: product.tags
            });

            this.#trackDefaultItemViewDataLayerEvent(product);
          }
        })
      ).subscribe()
    });
  }

  setUpProductCardSummary() {
    const productDetails = this.productsService.productSignal();
    localStorage.setItem('selectedProductId', productDetails.id);
    return {
      productDetails,
      settings: {
        isSummary: true
      }
    };
  }

  private setUpProductImages() {
    const product = this.productsService.productSignal();
    if (!product) return [];
    if (this.variant()?.image)
      return !!(this.variant()?.image || this.variant()?.additionalImages?.length) ? [this.variant()?.image, ...(this.variant()?.additionalImages || [])] : ['assets/images/product-placeholder.webp'];
    return !!(product?.img || product?.additionalImages?.length) ? [product?.img, ...product?.additionalImages] : ['assets/images/product-placeholder.webp'];
  }

  private setUpCarouselItems() {
    const product = this.productsService.productSignal();
    if (!product) return [];
    return product?.suggestedProducts?.map((suggestedProduct: any) => {
      suggestedProduct.image = 'assets/images/product-placeholder.webp';

      return ({
        content: {
          productDetails: { ...suggestedProduct, isSubscription: false },
          settings: {
            showSizes: false,
            showFlavors: false,
            showContentInfo: false,
            hideWhenWasOOS: true
          }
        }
      })
    });
  }

  gotoCategory() {
    this.router.navigate(['/shop/' + (this.product()?.category?.name || '').toLowerCase().replace(/\s/g, '-')]);
  }

  onSelectedVariant(e: any) {
    this.variant.set(e);
  }

  handleFavoriteChange() {
    this.productCard()
      .handleFavoriteChange();
  }

  prevDislikeProduct() {
    this.productCard()
      .prevDislikeProduct();
  }

  private setUpFavTotals() {
    const product = this.product();
    const variant = this.variant();
    if (variant)
      return variant.likeTotals || 0;

    return product.likeTotals || 0;
  }

  whatsInside(event: Event) {
    this.productCard()
      .openModalWhatsInside(event);
  }

  onChangeShowDisccountTag(event: boolean) {
    this.showDiscountTag.set(event);
  }

  #trackDefaultItemViewDataLayerEvent(product: Product) {
    let price = +product.price;
    let originalPrice = product.originalPrice || +product.price;
    // If the product has attributes:
    if (product.attributes?.length) {
      const attr = product.attributes[0];
      if (attr) {
        const value = attr.values?.[0] || null;
        if (value) {
          price = value.price;
          originalPrice = value.originalPrice || value.price;
        }
      }

    } else if (product.packages?.length) {
      // If the product has packages
      const pack = product.packages[0];
      if (pack) {
        price = pack.price * pack.quantity;
        originalPrice = (pack.originalPrice || pack.price) * pack.quantity;
      }
    }

    price = +(price.toFixed(2))
    originalPrice = +(originalPrice.toFixed(2))
    const discount = originalPrice ? +(originalPrice - price).toFixed(2) : 0;
    this.#dataLayerService.trackViewItemEvent({
      ecommerce: {
        currency: DATA_LAYER_ECOMMERCE_CURRENCY_OPTIONS.usd,
        value: price,
        items: [{
          item_id: product.id.toString(),
          item_name: product.name,
          price,
          discount,
          item_category: (product.category?.name || '').replace(/&/g, 'and'),
          item_category2: (product.subcategory?.name || '').replace(/&/g, 'and'),
          item_brand: product.producer?.name || '',
          quantity: 1
        }]
      }
    })
  }
}
